import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Context
import { ModalContext } from '../../context/ModalContext'

// Internal components
import Svg from '../Svg/Svg'
import Button from '../Button/Button'
import FormError from '../FormError/FormError'
import MaterialBlock from '../MaterialBlock/MaterialBlock'

// Utils
import getExerciseMaterialData from '../../utils/data/getExerciseMaterialData'
import { UserContext } from '../../context/UserContext'

const emptyClass = ' not-empty'

const FormItemDrawing = (props) => {
  const { item, onFormItemLoad, onFormItemChange, alternativeRoute, columnRef } = props
  const { register, watch, getValues, setValue, formState, errors } = useFormContext()

  // Context
  const modalContext = useContext(ModalContext)
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  // State
  const [isRequired, setIsRequired] = useState({ required: false })
  const [isLoaded, setIsLoaded] = useState(false)
  const [materialsBlockData, setMaterialsBlockData] = useState([])
  const [materialsCountData, setMaterialsCountData] = useState(false)

  const itemProps = item
  const itemRef = useRef()

  const itemInputRef = register(item.name, { required: false })
  const svgInputRef = register(item.svg.name, { required: false })

  const ballCountInputRef = register(item.ballCount.name, { required: false })
  const coneCountInputRef = register(item.coneCount.name, { required: false })
  const goalSmallCountInputRef = register(item.goalSmallCount.name, { required: false })
  const goalMediumCountInputRef = register(item.goalMediumCount.name, { required: false })
  const goalLargeCountInputRef = register(item.goalLargeCount.name, { required: false })
  const goalIndoorCountInputRef = register(item.goalIndoorCount.name, { required: false })
  const obstacles1CountInputRef = register(item.obstacles1Count.name, { required: false })
  const obstacles2CountInputRef = register(item.obstacles2Count.name, { required: false })
  const obstacles3CountInputRef = register(item.obstacles3Count.name, { required: false })
  const shirt1CountInputRef = register(item.shirt1Count.name, { required: false })
  const shirt2CountInputRef = register(item.shirt2Count.name, { required: false })
  const shirt3CountInputRef = register(item.shirt3Count.name, { required: false })
  const benchCountInputRef = register(item.benchCount.name, { required: false })
  const vaultingBoxCountInputRef = register(item.vaultingBoxCount.name, { required: false })
  const thickMatCountInputRef = register(item.thickMatCount.name, { required: false })
  const thinMatCountInputRef = register(item.thinMatCount.name, { required: false })
  const longMatCountInputRef = register(item.longMatCount.name, { required: false })
  const korfballCountInputRef = register(item.korfballCount.name, { required: false })
  const softballCountInputRef = register(item.softballCount.name, { required: false })

  const fieldWidthInputRef = register(item.fieldWidth.name, { required: false })
  const fieldLengthInputRef = register(item.fieldLength.name, { required: false })

  const fieldTypeInputRef = register(item.fieldType.name, { required: false })
  const materialsCountInputRef = register(item.materialsCount.name, isRequired)

  const itemValue = watch(item.name)
  const svgItemValue = watch(item.svg.name)

  const svgImageRef = useRef()

  const materialFormFieldMapping = {
    ball: 'ballCount',
    'witches-hat': 'coneCount',
    'goal-03': 'goalSmallCount',
    'goal-02': 'goalMediumCount',
    'goal-01': 'goalLargeCount',
    'goal-04': 'goalIndoorCount',
    'cone-yellow': 'obstacles1Count',
    'cone-orange': 'obstacles2Count',
    'cone-white': 'obstacles3Count',
    'shirt-attack': 'shirt1Count',
    'shirt-defend': 'shirt2Count',
    'shirt-neutral': 'shirt3Count',
    korfbal: 'softballCount',
    'korfbal-basket': 'korfballCount',
    bench: 'benchCount',
    'vaulting-box': 'vaultingBoxCount',
    'thin-mat': 'thinMatCount',
    'long-mat': 'longMatCount',
    'thick-mat': 'thickMatCount'
  }

  const skipMaterials = [
    'line-squiggley',
    'line-solid',
    'line-dotted',
    'line-shot',
    'shirt-annotation',
    'shirt-trainer',
    'marker',
    'field-text'
  ]

  useEffect(() => {
    if (!item.revealed && item.required && !alternativeRoute) setIsRequired({ required: requiredErrorMessage })
    setMaterials()
    modalContext.setModalCanPrompt(true)
    return () => modalContext.setModalCanPrompt(false)
  }, [])

  useEffect(() => {
    setFieldType()
  }, [item.fieldType.value])

  useEffect(() => {
    if (alternativeRoute) setIsRequired({ required: false })
  }, [alternativeRoute])

  useEffect(() => {
    if (!itemValue) return false
    if (!isLoaded) onFormItemLoad({ itemProps, itemRef, itemValue })
    else onFormItemChange({ itemProps, itemRef, itemValue })
    setIsLoaded(true)
  }, [itemValue])

  useEffect(() => {
    if (materialsCountData && materialsCountData !== 0) setValue(item.materialsCount.name, materialsCountData)
  }, [materialsCountData])

  // click handler to show drawing tool
  const showDrawingTool = (e) => {
    e.preventDefault()
    modalContext.setModalScrollTop(true)
    // iframe.current.src = process.env.NEXT_PUBLIC_DRAWING_APP_URL
    const drawingJson = getValues(item.name).toString() || null
    modalContext.setStateClass('is-loaded is-transitioning')
    modalContext.setModalFrameInput(drawingJson)
    modalContext.setModalFrameActive(true)
  }

  useEffect(() => {
    if (!modalContext.modalFrameOutput) return false
    iframeDataCallBack()
  }, [modalContext.modalFrameOutput])

  const iframeDataCallBack = (e) => {
    const drawingJsonString = modalContext.modalFrameOutput.drawing_JSON
    const drawingSvgString = modalContext.modalFrameOutput.drawing_SVG

    const drawingJsonObject = JSON.parse(drawingJsonString)
    // When no items are added to drawing by user, revert to empty state
    if (!drawingJsonObject.objects.length) {
      // Unset json
      setValue(item.name, '')

      // Unset svg data
      setValue(item.svg.name, '')

      return false
    }

    // Set json and drawing svg in form fields and show drawing
    setValue(item.name, drawingJsonString)

    // Set svg data
    setValue(item.svg.name, drawingSvgString)

    // Add svg data to img tag
    if (svgImageRef.current) svgImageRef.current.src = `data:image/svg+xml,${drawingSvgString}`

    // Set material form fields and show material count
    setMaterials()

    // Set field dimensions and show values
    setFieldDimensions()

    // Set field type for correct color scheme
    setFieldType()

    // clear output after parsing
    modalContext.setModalFrameOutput(null)
  }

  const setMaterials = () => {
    const materials = {}
    if (!getValues(item.name)) return null
    const drawingObject = JSON.parse(getValues(item.name))

    // Loop objects - These are all the items that are added to a drawing
    drawingObject.objects.forEach((object) => {
      // Skip materials we don't want to count
      if (!(object.variation === 'shirt-defend' && object.defaultIcon === 'assets/shirt-black.svg')) {
        if (!skipMaterials.includes(object.variation)) {
          if (!materials[object.variation]) materials[object.variation] = 0
          // Add the materials we do want to count
          materials[object.variation]++
        }
      }
    })

    // materials = alignShirtLabels(materials)

    updateMaterialFormFields(materials)
  }

  const setFieldType = () => {
    if (!getValues(item.name)) {
      if (userContext.userSession.sportType?.slug === 'handball') {
        setValue(item.fieldType.name, 'indoor')
        columnRef.current.classList.add('Modal--indoor')
        modalContext.setModalIsIndoor(true)
      }
      return null
    }

    const drawingObject = JSON.parse(getValues(item.name))
    const variation = drawingObject?.fieldVariation || false

    if (variation) {
      // Set the background color of the drawing within the modal
      if (variation.includes('field')) {
        setValue(item.fieldType.name, 'outdoor')
        columnRef.current.classList.remove('Modal--indoor')
        modalContext.setModalIsIndoor(false)
      } else {
        setValue(item.fieldType.name, 'indoor')
        columnRef.current.classList.add('Modal--indoor')
        modalContext.setModalIsIndoor(true)
      }

      if (variation) modalContext.setModalIsIndoor(!variation.includes('field'))
    } else {
      setValue(item.fieldType.name, 'outdoor')
      columnRef.current.classList.remove('Modal--indoor')
      modalContext.setModalIsIndoor(false)
    }
  }

  const setFieldDimensions = () => {
    const defaultFieldDimensions = [
      [0, 0],
      [45, 30],
      [30, 15],
      [20, 20],
      [15, 20]
    ]
    const drawingObject = JSON.parse(getValues(item.name))
    const currentWidth = getValues(item.fieldWidth.name)
    const currentHeight = getValues(item.fieldLength.name)

    let isDefaultDimension = false
    defaultFieldDimensions.forEach((fieldDimension) => {
      if (!isDefaultDimension) {
        isDefaultDimension =
          fieldDimension[0] === parseInt(currentHeight) && fieldDimension[1] === parseInt(currentWidth)
      }
    })

    if (drawingObject?.fieldDimensions?.[0] && isDefaultDimension) {
      setValue(item.fieldWidth.name, drawingObject.fieldDimensions[0])
    }

    if (drawingObject?.fieldDimensions?.[1] && isDefaultDimension) {
      setValue(item.fieldLength.name, drawingObject.fieldDimensions[1])
    }
  }

  const updateMaterialFormFields = (materials) => {
    // Note: The updated form values do get submitted, but you wont see the form values updated in the hidden form fields

    const materialsObj = {}
    let materialsCount = 0
    // update material count fields
    for (const material in materialFormFieldMapping) {
      const formField = materialFormFieldMapping[material]
      setValue(item[formField].name, materials[material])
      materialsObj[formField] = materials[material] ? materials[material] : 0
      materialsCount = materials[material] ? materialsCount + parseInt(materials[material]) : materialsCount
    }

    if (materialsObj !== {}) {
      const exerciseMaterialsData = getExerciseMaterialData(materialsObj, t)
      setMaterialsBlockData(exerciseMaterialsData)
      setMaterialsCountData(materialsCount)
    }
  }

  const updateFieldDimensionValue = (e, dimension) => {
    e.target.value = e.target.value.replace(/[^0-9/]/g, '') // Allows only numbers and '/'
    dimension.onChange(e)
  }

  return (
    <>
      <div
        ref={itemRef}
        className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
          itemValue ? emptyClass : ''
        }`}
      >
        <div className="FormItem-drawing">
          <div className="FormItem-figure">
            {!itemValue && (
              <div className="FormItem-emptyState" onClick={(e) => showDrawingTool(e)}>
                <Svg
                  modifiers={['FormItem-emptyImage']}
                  id={item.fieldType.value === 'outdoor' ? 'image-drawing' : 'image-drawing-indoor'}
                />
                <div className="FormItem-emptyMenu">
                  <Button
                    className="FormItem-emptyButton"
                    icon={{ id: 'icon-add' }}
                    modifiers={['Button--drawingEmpty']}
                  />
                  <span className="FormItem-emptyTitle">{t('FORMITEM_DRAWING__BTN_CREATEDRAWING')}</span>
                </div>
              </div>
            )}
            {/* {console.log(item)} */}
            {item.svgImageUrl && !svgItemValue && (
              <img src={`${item.svgImageUrl}?uncached=true`} className="drawingToolSvgImg" alt="Drawing Tool SVG" />
            )}
            {itemValue && svgItemValue && (
              <img
                src={`data:image/svg+xml,${svgItemValue}`}
                className="drawingToolSvgImg"
                alt="Drawing Tool SVG"
                ref={svgImageRef}
              />
            )}
          </div>
        </div>
        {formState.isSubmitted && <ErrorMessage errors={errors} name={item.materialsCount.name} as={<FormError />} />}

        {item && (
          <>
            <input
              type="hidden"
              id={item.name}
              name={item.name}
              defaultValue={item.value}
              className={`FormItem-input FormItem-input--${item.type}`}
              onChange={(e) => {
                itemInputRef.onChange(e)
              }}
              ref={itemInputRef.ref}
            />
            <input
              type="hidden"
              id={item.svg.name}
              name={item.svg.name}
              defaultValue={item.svg.value}
              className={'FormItem-input'}
              onChange={(e) => {
                svgInputRef.onChange(e)
              }}
              ref={svgInputRef.ref}
            />

            <input
              type="hidden"
              id={item.ballCount.name}
              name={item.ballCount.name}
              defaultValue={item.ballCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                ballCountInputRef.onChange(e)
              }}
              ref={ballCountInputRef.ref}
            />

            <input
              type="hidden"
              id={item.ballCount.name}
              name={item.ballCount.name}
              defaultValue={item.ballCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                ballCountInputRef.onChange(e)
              }}
              ref={ballCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.coneCount.name}
              name={item.coneCount.name}
              defaultValue={item.coneCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                coneCountInputRef.onChange(e)
              }}
              ref={coneCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.goalSmallCount.name}
              name={item.goalSmallCount.name}
              defaultValue={item.goalSmallCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                goalSmallCountInputRef.onChange(e)
              }}
              ref={goalSmallCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.goalMediumCount.name}
              name={item.goalMediumCount.name}
              defaultValue={item.goalMediumCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                goalMediumCountInputRef.onChange(e)
              }}
              ref={goalMediumCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.goalLargeCount.name}
              name={item.goalLargeCount.name}
              defaultValue={item.goalLargeCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                goalLargeCountInputRef.onChange(e)
              }}
              ref={goalLargeCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.goalIndoorCount.name}
              name={item.goalIndoorCount.name}
              defaultValue={item.goalIndoorCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                goalIndoorCountInputRef.onChange(e)
              }}
              ref={goalIndoorCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.obstacles1Count.name}
              name={item.obstacles1Count.name}
              defaultValue={item.obstacles1Count.value}
              className={'FormItem-input'}
              onChange={(e) => {
                obstacles1CountInputRef.onChange(e)
              }}
              ref={obstacles1CountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.obstacles2Count.name}
              name={item.obstacles2Count.name}
              defaultValue={item.obstacles2Count.value}
              className={'FormItem-input'}
              onChange={(e) => {
                obstacles2CountInputRef.onChange(e)
              }}
              ref={obstacles2CountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.obstacles3Count.name}
              name={item.obstacles3Count.name}
              defaultValue={item.obstacles3Count.value}
              className={'FormItem-input'}
              onChange={(e) => {
                obstacles3CountInputRef.onChange(e)
              }}
              ref={obstacles3CountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.shirt1Count.name}
              name={item.shirt1Count.name}
              defaultValue={item.shirt1Count.value}
              className={'FormItem-input'}
              onChange={(e) => {
                shirt1CountInputRef.onChange(e)
              }}
              ref={shirt1CountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.shirt2Count.name}
              name={item.shirt2Count.name}
              defaultValue={item.shirt2Count.value}
              className={'FormItem-input'}
              onChange={(e) => {
                shirt2CountInputRef.onChange(e)
              }}
              ref={shirt2CountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.shirt3Count.name}
              name={item.shirt3Count.name}
              defaultValue={item.shirt3Count.value}
              className={'FormItem-input'}
              onChange={(e) => {
                shirt3CountInputRef.onChange(e)
              }}
              ref={shirt3CountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.benchCount.name}
              name={item.benchCount.name}
              defaultValue={item.benchCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                benchCountInputRef.onChange(e)
              }}
              ref={benchCountInputRef.ref}
            />

            <input
              type="hidden"
              id={item.vaultingBoxCount.name}
              name={item.vaultingBoxCount.name}
              defaultValue={item.vaultingBoxCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                vaultingBoxCountInputRef.onChange(e)
              }}
              ref={vaultingBoxCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.longMatCount.name}
              name={item.longMatCount.name}
              defaultValue={item.longMatCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                longMatCountInputRef.onChange(e)
              }}
              ref={longMatCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.thickMatCount.name}
              name={item.thickMatCount.name}
              defaultValue={item.thickMatCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                thickMatCountInputRef.onChange(e)
              }}
              ref={thickMatCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.thinMatCount.name}
              name={item.thinMatCount.name}
              defaultValue={item.thinMatCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                thinMatCountInputRef.onChange(e)
              }}
              ref={thinMatCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.korfballCount.name}
              name={item.korfballCount.name}
              defaultValue={item.korfballCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                korfballCountInputRef.onChange(e)
              }}
              ref={korfballCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.softballCount.name}
              name={item.softballCount.name}
              defaultValue={item.softballCount.value}
              className={'FormItem-input'}
              onChange={(e) => {
                softballCountInputRef.onChange(e)
              }}
              ref={softballCountInputRef.ref}
            />

            <input
              type="hidden"
              id={item.fieldWidth.name}
              name={item.fieldWidth.name}
              defaultValue={item.fieldWidth.value}
              className={'FormItem-input'}
              onChange={(e) => {
                fieldWidthInputRef.onChange(e)
              }}
              ref={fieldWidthInputRef.ref}
            />
            <input
              type="hidden"
              id={item.fieldLength.name}
              name={item.fieldLength.name}
              defaultValue={item.fieldLength.value}
              className={'FormItem-input'}
              onChange={(e) => {
                fieldLengthInputRef.onChange(e)
              }}
              ref={fieldLengthInputRef.ref}
            />

            <input
              type="hidden"
              id={item.materialsCount.name}
              name={item.materialsCount.name}
              className={'FormItem-input'}
              onChange={(e) => {
                materialsCountInputRef.onChange(e)
              }}
              ref={materialsCountInputRef.ref}
            />
            <input
              type="hidden"
              id={item.fieldType.name}
              name={item.fieldType.name}
              defaultValue={item.fieldType.value}
              className={'FormItem-input'}
              onChange={(e) => {
                fieldTypeInputRef.onChange(e)
              }}
              ref={fieldTypeInputRef.ref}
            />
          </>
        )}
        {itemValue && (
          <div className="FormItem-drawingMenu">
            <div className="FormItem-drawingMenuBar FormItem-drawingMenuBar--top">
              {materialsBlockData && (
                <MaterialBlock data={materialsBlockData} modifiers={['MaterialBlock--formItemDrawing']} />
              )}
            </div>
            <div className="FormItem-drawingMenuBar FormItem-drawingMenuBar--bottom">
              <div className="FormItem-drawingMenuColumn">
                <div className="FormItem-drawingSizing">
                  <div className="FormItem-drawingSizingLabel">
                    <span>{t('FORMITEM_DRAWING__SIZE')}</span>
                  </div>
                  <div className="FormItem-drawingSizingFields">
                    <input
                      type="text"
                      id={item.fieldLength.name}
                      name={item.fieldLength.name}
                      className={'FormItem-drawingSizeInput'}
                      onChange={(e) => {
                        updateFieldDimensionValue(e, fieldLengthInputRef)
                      }}
                      ref={fieldLengthInputRef.ref}
                    />
                    <span data-label={t('FORMITEM_DRAWING__LENGTH')}></span>
                    <input
                      type="text"
                      id={item.fieldWidth.name}
                      name={item.fieldWidth.name}
                      className={'FormItem-drawingSizeInput'}
                      onChange={(e) => {
                        updateFieldDimensionValue(e, fieldWidthInputRef)
                      }}
                      ref={fieldWidthInputRef.ref}
                    />
                    <span data-label={t('FORMITEM_DRAWING__WIDTH')}></span>
                  </div>
                </div>
              </div>
              <div className="FormItem-drawingMenuColumn">
                <Button
                  label={t('FORMITEM_DRAWING__BTN_EDITDRAWING')}
                  icon={{ id: 'icon-chevron-right' }}
                  modifiers={['Button--formItemDrawing']}
                  onClick={(e) => showDrawingTool(e)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

FormItemDrawing.propTypes = {
  item: PropTypes.object,
  onFormItemChange: PropTypes.func,
  onFormItemLoad: PropTypes.func,
  alternativeRoute: PropTypes.any,
  columnRef: PropTypes.any
}

export default FormItemDrawing
